// Key code constants
var KEY_RETURN = 13;
var KEY_ESCAPE = 27;
var KEY_SPACE = 32;
var KEY_LEFT = 37;
var KEY_UP = 38;
var KEY_RIGHT = 39;
var KEY_DOWN = 40;

var config = {
  transition: null,
};

//

var script = {
  name: 'VueAccessibleSelect',
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      required: true,
    },
    transition: {
      type: Object,
      default: () => config.transition || {},
    },
    label: String,
    placeholder: String,
    disabled: Boolean,
  },
  data() {
    const { _uid } = this;

    return {
      open: false,
      timeout: null,
      printedText: '',
      localId_: _uid,
    }
  },
  computed: {
    labelId() {
      return this.label ? `v-select-label-${this.localId_}` : false
    },
    buttonId() {
      return `v-select-button-${this.localId_}`
    },
    ariaExpanded() {
      return this.open ? 'true' : 'false'
    },
    className() {
      return {
        'v-select--opened': this.open,
        'v-select--disabled': this.disabled,
      }
    },
    currentOption() {
      return (
        Array.isArray(this.options) &&
        this.options.find(option => option.value === this.value)
      )
    },
    currentOptionIndex() {
      return this.options.findIndex(option => option === this.currentOption)
    },
    optionsHasValue() {
      return (
        this.options.findIndex(option => option.value === this.value) !== -1
      )
    },
  },
  watch: {
    open(val) {
      if (val) {
        setTimeout(() => {
          document.addEventListener('click', this.clickListener);

          // if list is present in DOM
          const { list } = this.$refs;

          if (list) {
            list.focus();

            // * if option is selected, then scroll to it
            const { value } = this;
            if (value || value === 0) {
              this.scrollToSelected();
            }
          }

          this.$emit('open');
        }, 0);
      } else {
        document.removeEventListener('click', this.clickListener);
        this.$emit('close');
      }
    },
    value(val) {
      // * if option is selected, then scroll to it
      if (val || val == 0) {
        this.scrollToSelected();
      }
    },
  },
  /*
   * SSR Safe Client Side ID attribute generation
   * id's can only be generated client side, after mount.
   * this._uid is not synched between server and client.
   */
  mounted() {
    this.$nextTick(() => {
      // Update dom with auto ID after dom loaded to prevent
      // SSR hydration errors.
      this.localId_ = this._uid;
    });
  },
  methods: {
    toggle() {
      this.open = !this.open;
    },
    emit(val) {
      this.$emit('input', val);
      this.$emit('change', val);
    },
    clickListener(e) {
      const { target } = e;
      const closestBtn = target.closest('.v-select__btn');
      const closestList = target.closest('.v-select__list');

      if (closestList === this.$refs.list || closestBtn === this.$refs.button) ; else {
        this.open = false;
      }
    },
    isSelected(option) {
      return this.value === option.value
    },
    clickHandler(option) {
      this.emit(option.value);
      this.open = false;
    },
    keydownHandler(e) {
      if (e.keyCode === KEY_ESCAPE) {
        return
      }
      // prevent from default scrolling

      if (
        [KEY_SPACE, KEY_LEFT, KEY_UP, KEY_RIGHT, KEY_DOWN].indexOf(e.keyCode) >
        -1
      ) {
        e.preventDefault();
      }

      const { currentOptionIndex } = this;
      // if neither option is selected then select the first

      if (currentOptionIndex === -1) {
        this.emit(this.options[0].value);
        return
      }

      switch (e.keyCode) {
        case KEY_UP:
          if (currentOptionIndex !== 0)
            this.emit(this.options[currentOptionIndex - 1].value);
          break
        case KEY_DOWN:
          if (currentOptionIndex !== this.options.length - 1)
            this.emit(this.options[currentOptionIndex + 1].value);
          break
        case KEY_RETURN:
          setTimeout(() => {
            this.open = false;
            this.$refs.button.focus();
          }, 0);
          break
      }
    },
    getOptionId(option) {
      return `v-select-option-${this.options.indexOf(option)}_${this.localId_}`
    },
    setFirstSelected() {
      this.emit(this.options[0].value);
    },
    setLastSelected() {
      this.emit(this.options[this.options.length - 1].value);
    },
    escapeHandler() {
      this.open = false;
      this.$refs.button.focus();
    },
    printHandler(e) {
      this.printedText += String.fromCharCode(e.keyCode);

      this.selectByText(this.printedText);

      clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.printedText = '';
      }, 500);
    },
    selectByText(text) {
      for (let option of this.options) {
        if (
          String(option.label)
            .toUpperCase()
            .startsWith(text)
        ) {
          this.emit(option.value);
          return
        }
      }
    },
    scrollToSelected() {
      // * get current option DOM node
      if (Array.isArray(this.$refs.options)) {
        const currentOption = this.$refs.options[this.currentOptionIndex];

        if (currentOption) {
          const { offsetTop, clientHeight } = currentOption;

          const { list } = this.$refs;

          const currentVisibleArea = list.scrollTop + list.clientHeight;

          if (offsetTop < list.scrollTop) {
            list.scrollTop = offsetTop;
          } else if (offsetTop + clientHeight > currentVisibleArea) {
            list.scrollTop = offsetTop - list.clientHeight + clientHeight;
          }
        }
      }
    },
    buttonBlurHandler(e) {
      let target = e.relatedTarget;
      if (target === null) { target = document.activeElement; }
      if (target !== this.$refs.list && this.open) {
        this.open = false;
      }
    },
    menuBlurHandler(e) {
      let target = e.relatedTarget;
      if (target === null) { target = document.activeElement; }
      if (target !== this.$refs.button) {
        this.open = false;
      }
    },
    hasSlot(name) {
      return Boolean(this.$slots[name]) || Boolean(this.$scopedSlots[name])
    },
  },
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  var options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;

  if (moduleIdentifier) {
    // server build
    hook = function hook(context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function () {
      style.call(this, createInjectorShadow(this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      var originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

var normalizeComponent_1 = normalizeComponent;

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "v-select", class: _vm.className }, [
    _vm.hasSlot("label") || _vm.label
      ? _c(
          "span",
          { staticClass: "v-select__label", attrs: { id: _vm.labelId } },
          [
            _vm._t("label", function() {
              return [_vm._v(_vm._s(_vm.label) + ":")]
            })
          ],
          2
        )
      : _vm._e(),
    _c(
      "div",
      { staticClass: "v-select__inner" },
      [
        _c(
          "button",
          {
            ref: "button",
            staticClass: "v-select__btn",
            attrs: {
              id: _vm.buttonId,
              disabled: _vm.disabled,
              "aria-expanded": _vm.ariaExpanded,
              "aria-labelledby":
                (_vm.labelId ? _vm.labelId : "") + " " + _vm.buttonId,
              type: "button",
              "aria-haspopup": "listbox"
            },
            on: { click: _vm.toggle, blur: _vm.buttonBlurHandler }
          },
          [
            _vm.hasSlot("prepend")
              ? _c(
                  "span",
                  { staticClass: "v-select__prepend" },
                  [_vm._t("prepend")],
                  2
                )
              : _vm._e(),
            ((_vm.placeholder || _vm.hasSlot("placeholder")) &&
              _vm.value === undefined) ||
            !_vm.optionsHasValue
              ? _c(
                  "span",
                  { staticClass: "v-select__placeholder" },
                  [
                    _vm._t(
                      "placeholder",
                      function() {
                        return [_vm._v(_vm._s(_vm.placeholder))]
                      },
                      { placeholder: _vm.placeholder }
                    )
                  ],
                  2
                )
              : _vm._e(),
            _c(
              "span",
              { staticClass: "v-select__selected" },
              [
                _vm.value !== undefined && _vm.optionsHasValue
                  ? _vm._t(
                      "selected",
                      function() {
                        return [
                          _vm._v(
                            _vm._s(
                              _vm.currentOption
                                ? _vm.currentOption.label
                                : _vm.value
                            )
                          )
                        ]
                      },
                      { value: _vm.value, option: _vm.currentOption }
                    )
                  : _vm._e()
              ],
              2
            ),
            _c(
              "span",
              { staticClass: "v-select__arrow" },
              [
                _vm._t("arrow", function() {
                  return [
                    _c("svg", { attrs: { viewBox: "0 0 255 255" } }, [
                      _c("path", { attrs: { d: "M0 64l128 127L255 64z" } })
                    ])
                  ]
                })
              ],
              2
            )
          ]
        ),
        _c(
          "transition",
          {
            attrs: {
              name: _vm.transition ? _vm.transition.name : "",
              mode: _vm.transition ? _vm.transition.mode : ""
            }
          },
          [
            _vm.open
              ? _c(
                  "div",
                  { staticClass: "v-select__menu" },
                  [
                    _vm.options && _vm.options.length
                      ? _c(
                          "ul",
                          {
                            ref: "list",
                            staticClass: "v-select__list",
                            attrs: {
                              "aria-activedescendant":
                                _vm.value && _vm.getOptionId(_vm.value),
                              "aria-labelledby": _vm.labelId,
                              role: "listbox",
                              tabindex: "-1"
                            },
                            on: {
                              keydown: _vm.keydownHandler,
                              keyup: [
                                function($event) {
                                  if (
                                    !$event.type.indexOf("key") &&
                                    $event.keyCode !== 35
                                  ) {
                                    return null
                                  }
                                  return _vm.setLastSelected.apply(
                                    null,
                                    arguments
                                  )
                                },
                                function($event) {
                                  if (
                                    !$event.type.indexOf("key") &&
                                    $event.keyCode !== 36
                                  ) {
                                    return null
                                  }
                                  return _vm.setFirstSelected.apply(
                                    null,
                                    arguments
                                  )
                                },
                                function($event) {
                                  if (
                                    !$event.type.indexOf("key") &&
                                    _vm._k(
                                      $event.keyCode,
                                      "esc",
                                      27,
                                      $event.key,
                                      ["Esc", "Escape"]
                                    )
                                  ) {
                                    return null
                                  }
                                  return _vm.escapeHandler.apply(
                                    null,
                                    arguments
                                  )
                                },
                                _vm.printHandler
                              ],
                              blur: _vm.menuBlurHandler
                            }
                          },
                          _vm._l(_vm.options, function(option, index) {
                            return _c(
                              "li",
                              {
                                key: index,
                                ref: "options",
                                refInFor: true,
                                staticClass: "v-select__option",
                                class: {
                                  "v-select__option--selected": _vm.isSelected(
                                    option
                                  )
                                },
                                attrs: {
                                  id: _vm.getOptionId(option),
                                  role: "option",
                                  "aria-selected": _vm.isSelected(option)
                                    ? "true"
                                    : "false"
                                },
                                on: {
                                  click: function($event) {
                                    return _vm.clickHandler(option)
                                  }
                                }
                              },
                              [
                                _vm._t(
                                  "option",
                                  function() {
                                    return [
                                      _c("span", [_vm._v(_vm._s(option.label))])
                                    ]
                                  },
                                  { option: option, value: _vm.value }
                                )
                              ],
                              2
                            )
                          }),
                          0
                        )
                      : [
                          _c(
                            "div",
                            { staticClass: "v-select__no-options" },
                            [
                              _vm._t("no-optioms", function() {
                                return [
                                  _c("span", [_vm._v("No options provided")])
                                ]
                              })
                            ],
                            2
                          )
                        ]
                  ],
                  2
                )
              : _vm._e()
          ]
        )
      ],
      1
    )
  ])
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  

  
  var VueAccessibleSelect = normalizeComponent_1(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    undefined,
    undefined
  );

const Plugin = {
  install(Vue) {
    // Make sure that plugin can be installed only once
    if (this.installed) {
      return
    }

    Vue.component('VueAccessibleSelect', VueAccessibleSelect);
  },
};

export { VueAccessibleSelect, config, Plugin as default };
